import type { MeasuringPoint, MeasuringPointWithProject, ProjectOverviewRow } from '../types';
import { isNonNullable } from '../../../../utils/typeGuards';
import { hasAtLeastPermission, PERMISSION } from '../../sharing/utils';
import { createMeasuringPointInProjectUrl } from '../../measuringPoint/utils';

interface MeasuringPointProjectContext {
    projectId: string | null;
    projectName: string | null;
}

/** The measuring points of an overview query, without the generated types' fragment masking. */
export function unmaskOverviewMeasuringPoints(
    measuringPoints: ReadonlyArray<unknown> | null | undefined
): MeasuringPoint[] {
    return (measuringPoints ?? []).filter(isNonNullable) as MeasuringPoint[];
}

export function mapOverviewQueryMeasuringPoints(
    measuringPoints: ReadonlyArray<unknown> | null | undefined,
    { projectId, projectName }: MeasuringPointProjectContext
): MeasuringPointWithProject[] {
    return unmaskOverviewMeasuringPoints(measuringPoints).map((measuringPoint) => ({
        ...measuringPoint,
        projectId,
        projectName,
    }));
}

/**
 * Where to add a measuring point to a project, or `null` when that is not allowed: the project
 * is archived, or the user only has view access.
 */
export function getNewMeasuringPointUrl(project: ProjectOverviewRow): string | null {
    return !project.archived && hasAtLeastPermission(project.permission, PERMISSION.FullAccess)
        ? createMeasuringPointInProjectUrl(project.swarmType, project.projectId)
        : null;
}

export function getMeasuringPointRowKey(measuringPoint: MeasuringPointWithProject): string {
    return `${measuringPoint.swarmType}-${measuringPoint.id}`;
}

export function uniqueMeasuringPoints(
    measuringPoints: MeasuringPointWithProject[]
): MeasuringPointWithProject[] {
    return Array.from(
        measuringPoints
            .reduce((seen, measuringPoint) => {
                const key = getMeasuringPointRowKey(measuringPoint);
                if (!seen.has(key)) {
                    seen.set(key, measuringPoint);
                }
                return seen;
            }, new Map<string, MeasuringPointWithProject>())
            .values()
    );
}
